import type { Permission } from '@shared/types';

/**
 * 导航项。
 *
 * `permission` 只用于隐藏「点进去也用不了」的入口（体验层），
 * 真正的鉴权在 Worker 的 requirePermission 里。
 */
export interface NavItem {
  to: string;
  label: string;
  permission: Permission;
  description: string;
  /** 24×24 的 SVG path，fill: currentColor */
  icon: string;
  /** 精确匹配（用于 `/`，否则任何路径都会点亮它） */
  end?: boolean;
}

const ICON_HOME = 'M12 3l9 7.5V21h-6v-6H9v6H3V10.5L12 3z';
const ICON_BOARD = 'M3 3h8v18H3V3zm10 0h8v11h-8V3z';
const ICON_MINE = 'M12 11a4 4 0 100-8 4 4 0 000 8zm0 2c-4.2 0-8 2.6-8 6h16c0-3.4-3.8-6-8-6z';
const ICON_STATS = 'M3 20h18v2H3v-2zM6 18h3V9H6v9zm5 0h3V4h-3v14zm5 0h3v-6h-3v6z';
const ICON_USERS =
  'M9 11.5a3.75 3.75 0 100-7.5 3.75 3.75 0 000 7.5zm7.5-.5a3 3 0 100-6 3 3 0 010 6zM2 20c0-3.3 3.1-5.5 7-5.5s7 2.2 7 5.5H2zm14.9 0c-.2-2-1-3.7-2.2-5 3.1-.4 6.3 1.2 6.3 5h-4.1z';
const ICON_SETTINGS =
  'M12 8.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zM4.6 12c0-.4 0-.8.1-1.2L2.5 9.1l1.8-3.1 2.5 1c.6-.5 1.3-.9 2-1.2L9.1 3h3.8l.3 2.8c.7.3 1.4.7 2 1.2l2.5-1 1.8 3.1-2.2 1.7c.1.4.1.8.1 1.2s0 .8-.1 1.2l2.2 1.7-1.8 3.1-2.5-1c-.6.5-1.3.9-2 1.2l-.3 2.8H9.1l-.3-2.8c-.7-.3-1.4-.7-2-1.2l-2.5 1-1.8-3.1 2.2-1.7c-.1-.4-.1-.8-.1-1.2z';
const ICON_AUDIT = 'M5 3h9l5 5v13H5V3zm7 1.6V9h4.4L12 4.6zM7.5 12.5h9v1.8h-9v-1.8zm0 4h9v1.8h-9v-1.8z';
const ICON_USAGE =
  'M12 2a10 10 0 100 20 10 10 0 000-20zm1 5v5.6l4 2.4-1 1.7-5-3V7h2z';

export const NAV_ITEMS: readonly NavItem[] = [
  {
    to: '/',
    label: '主页',
    permission: 'idea.view',
    description: '协作数据总览与常用入口',
    icon: ICON_HOME,
    end: true,
  },
  {
    to: '/board',
    label: '创意看板',
    permission: 'idea.view',
    description: '按状态查看全部创意，从待验证到已上线',
    icon: ICON_BOARD,
  },
  {
    to: '/mine',
    label: '我的',
    permission: 'idea.view',
    description: '我提的、我参与的、我认领的',
    icon: ICON_MINE,
  },
  {
    to: '/stats',
    label: '数据看板',
    permission: 'idea.view',
    description: '创意总量、AI 通过率与最近动态',
    icon: ICON_STATS,
  },
  {
    to: '/admin/users',
    label: '用户管理',
    permission: 'user.view',
    description: '账号、角色与口令重置',
    icon: ICON_USERS,
  },
  {
    to: '/admin/settings',
    label: '系统设置',
    permission: 'settings.manage',
    description: 'AI 模型与额度、分类标签、团队能力清单',
    icon: ICON_SETTINGS,
  },
  {
    to: '/admin/audit',
    label: '审计日志',
    permission: 'audit.view',
    description: '登录记录与管理员动作',
    icon: ICON_AUDIT,
  },
  {
    to: '/admin/usage',
    label: '资源用量',
    permission: 'usage.view',
    description: '按成员统计 AI 研判消耗的模型额度',
    icon: ICON_USAGE,
  },
];

/** 顶部标题取「最长匹配」：`/admin/users` 不会被 `/` 抢先匹配成「主页」。 */
export function currentSectionLabel(items: readonly NavItem[], pathname: string): string {
  let best: NavItem | null = null;
  for (const item of items) {
    if (item.end) {
      if (pathname === item.to) return item.label;
      continue;
    }
    if (!pathname.startsWith(item.to)) continue;
    if (!best || item.to.length > best.to.length) best = item;
  }
  // 详情、新建这类不在导航里的深层页面，回落到它们所属的模块
  if (pathname.startsWith('/ideas')) return '创意看板';
  return best?.label ?? '';
}
